import { useSearchParams } from "react-router-dom";
import { CalendarRange, CreditCard, FileSpreadsheet, LineChart } from "lucide-react";
import { WIDGETS, WidgetCard } from "@/components/dashboard/widgets";
import { DemoBadge } from "@/components/common/DemoBadge";
import { BankCard } from "@/components/finance/BankCard";
import { ExpensesCard } from "@/components/finance/ExpensesCard";
import { BudgetsCard } from "@/components/finance/BudgetsCard";
import { BudgetOverview } from "@/components/finance/BudgetOverview";
import { SuppliersDirectory } from "@/components/finance/SuppliersDirectory";
import { RecurringCard } from "@/components/finance/RecurringCard";
import { PersoChargesPlan } from "@/components/business/BizCharges";
import { OperationsExplorer } from "@/components/reports/OperationsExplorer";
import { BankFeesPanel } from "@/components/reports/BankFeesPanel";
import { ReportView, ReportsList } from "@/components/reports/ReportView";
import { ForecastCard } from "@/components/planning/ForecastCard";
import { ReconcileCard } from "@/components/planning/ReconcileCard";
import { AnnualPlan } from "@/components/planning/AnnualPlan";
import { PaymentMeans } from "@/components/finance/PaymentMeans";
import { useState } from "react";
import { ModuleCard } from "@/components/common/ModuleCard";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/common/EmptyState";
import { useHousehold } from "@/context/HouseholdContext";
import { useDashboard } from "@/hooks/use-dashboard";

const PLANNED = [
  { icon: CreditCard, title: "Comptes & transactions", text: "Saisie manuelle ou agrégation bancaire. L'application fonctionne aussi sans connexion bancaire." },
  { icon: CalendarRange, title: "Revenus & reste à vivre", text: "Enveloppes avancées, reste à vivre, bulletins de paie." },
  { icon: LineChart, title: "Prévisions & objectifs", text: "Projection de trésorerie et préparation des projets." },
  { icon: FileSpreadsheet, title: "Dossiers partageables", text: "Dossiers financiers partagés volontairement, révocables." },
];
const FINANCE_ROLES = ["owner", "admin", "adult"];

const Show = ({ id, data }) => {
  const d = WIDGETS[id];
  return <WidgetCard id={`finances-${id}`} title={d.title} icon={d.icon} dark={d.dark} tone={d.tone} className={d.span}><d.Comp data={data} /></WidgetCard>;
};

const Personal = () => {
  const { data, isLoading, isError } = useDashboard();
  return (
    <div className="space-y-8">
      <BudgetOverview scope="personal" title="Mon budget personnel" />
      <ForecastCard scope="personal" sid="me" />
      {isError ? <EmptyState title="Données indisponibles" testid="finances-error" />
        : isLoading || !data ? <Skeleton className="h-48 rounded-2xl" /> : (
        <div className="grid gap-4 md:grid-cols-2">
          <Show id="goals" data={data} />
        </div>
      )}
      <BudgetsCard />
      <AnnualPlan scope="personal" sid="me" />
      <ReconcileCard scope="personal" sid="me" />
      <RecurringCard scope="personal" />
      <PersoChargesPlan scope="personal" />
      <BankCard />
      <ExpensesCard />
    </div>
  );
};

const HouseholdBudget = ({ h }) => {
  if (!FINANCE_ROLES.includes(h.role)) return <EmptyState icon={CreditCard} title="Réservé aux adultes" text="Le budget du foyer est visible par les adultes du foyer." testid="hh-budget-forbidden" />;
  const base = `/finance/household/${h.id}`;
  return (
    <div className="space-y-8">
      <BudgetOverview key={h.id} scope="household" householdId={h.id} title={`Budget du foyer · ${h.name}`} />
      <ForecastCard key={`f-${h.id}`} scope="household" sid={h.id} title="Prévision de fin de mois du foyer" />
      <BudgetsCard base={base} canEdit={["owner", "admin"].includes(h.role)} title="Plafonds du foyer" />
      <RecurringCard key={`rc-${h.id}`} scope="household" householdId={h.id} />
      <PersoChargesPlan key={`cp-${h.id}`} scope="household" householdId={h.id} />
      <AnnualPlan key={`p-${h.id}`} scope="household" sid={h.id} />
      <ReconcileCard key={`r-${h.id}`} scope="household" sid={h.id} />
      <ExpensesCard base={base} tid="hh-expense" title="Dépenses communes" text="Dépenses partagées du foyer, visibles par tous les adultes." />
    </div>
  );
};

const ReportsTab = () => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-8">
      <ReportsList onOpen={setOpen} />
      <BankFeesPanel onReport={(r) => setOpen(r.id)} />
      <OperationsExplorer onReport={(r) => setOpen(r.id)} />
      <ReportView id={open} onClose={() => setOpen(null)} />
    </div>
  );
};

export default function Finances() {
  const { data } = useDashboard();
  const { active } = useHousehold();
  const [params, setParams] = useSearchParams();
  const tab = ["perso", "foyer", "paiement", "fournisseurs", "rapports"].includes(params.get("tab")) ? params.get("tab") : "perso";
  return (
    <div className="space-y-8" data-testid="finances-page">
      <PageHeader eyebrow="CASHMY Finance" title="Finances" text="Budget personnel privé, budget commun du foyer et annuaire de vos fournisseurs." actions={data?.is_demo && <DemoBadge />} />
      {!active ? <EmptyState icon={CreditCard} title="Aucun foyer" text="Les finances personnelles sont rattachées à un foyer." testid="finances-no-household" /> : (
        <Tabs value={tab} onValueChange={(t) => setParams({ tab: t }, { replace: true })}>
          <TabsList className="no-scrollbar flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-2xl bg-muted p-1.5 sm:w-auto" data-testid="finances-tabs">
            <TabsTrigger value="perso" className="rounded-xl" data-testid="finances-tab-perso">Mon budget</TabsTrigger>
            <TabsTrigger value="foyer" className="rounded-xl" data-testid="finances-tab-foyer">Budget du foyer</TabsTrigger>
            <TabsTrigger value="paiement" className="rounded-xl" data-testid="finances-tab-paiement">Moyens de paiement</TabsTrigger>
            <TabsTrigger value="fournisseurs" className="rounded-xl" data-testid="finances-tab-fournisseurs">Mes fournisseurs</TabsTrigger>
            <TabsTrigger value="rapports" className="rounded-xl" data-testid="finances-tab-rapports">Rapports</TabsTrigger>
          </TabsList>
          <TabsContent value="perso" className="mt-6"><Personal /></TabsContent>
          <TabsContent value="foyer" className="mt-6"><HouseholdBudget h={active} /></TabsContent>
          <TabsContent value="paiement" className="mt-6"><PaymentMeans /></TabsContent>
          <TabsContent value="fournisseurs" className="mt-6"><SuppliersDirectory /></TabsContent>
          <TabsContent value="rapports" className="mt-6"><ReportsTab /></TabsContent>
        </Tabs>
      )}
      <section>
        <h2 className="text-lg font-bold">Bientôt dans Finances</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PLANNED.map((m, i) => <ModuleCard key={m.title} {...m} brief={2} delay={i * 50} testid={`finances-module-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
